import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Collapse,
  Grid,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Business,
  LocationOn,
  Phone,
  Email,
  Language,
  Schedule,
  Payment,
  CheckCircle,
  Visibility,
} from '@mui/icons-material';
import TabDescription from './TabDescription';
import axios from 'axios';
import { useRegionContext } from '../../region/RegionContext';
import { useBusinessData } from '../../context/BusinessDataContext';
import MediaPreviewDialog from './MediaPreviewDialog';

const PAGE_SIZE = 25;

const Verified = () => {
  const { endpoints } = useRegionContext();
  const { verifiedOnly, verifiedLoading: loading, verifiedError: error, loadVerified, refreshVerified } = useBusinessData();
  const [expandedId, setExpandedId] = useState(null);
  const [sendingStripe, setSendingStripe] = useState(null);
  const [previewVenue, setPreviewVenue] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (verifiedOnly === null) loadVerified();
  }, [verifiedOnly, loadVerified]);

  const venues = verifiedOnly || [];

  const handleToggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleSendStripeOnboarding = async (venue) => {
    if (!window.confirm(`Send Stripe onboarding link to ${venue.bizName}?`)) {
      return;
    }

    try {
      setSendingStripe(venue.id);

      await axios.post(
        endpoints.sendStripeOnboarding,
        {
          bizId: venue.id,
          bizName: venue.bizName,
          bizEmail: venue.bizEmail || venue.userEmail,
        }
      );

      alert(`Stripe onboarding link sent to ${venue.bizName}.`);
    } catch (err) {
      console.error('Failed to send Stripe onboarding:', err);
      alert('Failed to send Stripe onboarding link. Please try again.');
    } finally {
      setSendingStripe(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading verified venues...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (venues.length === 0) {
    return (
      <Alert severity="info">
        No verified venues awaiting Stripe onboarding
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Verified (Awaiting Stripe): {venues.length}
        </Typography>
        <Button onClick={refreshVerified} variant="outlined">
          Refresh
        </Button>
      </Box>
      <TabDescription>
        Venues that have passed verification but haven't completed Stripe payment setup yet. Data comes from
        the Business collection where bizVerified is true and bizStripeId is missing. These venues are approved
        to go live but can't accept orders until Stripe onboarding is complete. Send onboarding links and
        follow up with venue owners who haven't connected their payment account.
      </TabDescription>

      {/* Pagination controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={page === 0}
          onClick={() => { setPage(p => p - 1); setExpandedId(null); }}
        >
          Previous
        </Button>
        <Typography variant="body2">
          Page {page + 1} of {Math.ceil(venues.length / PAGE_SIZE)} ({venues.length} total)
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={(page + 1) * PAGE_SIZE >= venues.length}
          onClick={() => { setPage(p => p + 1); setExpandedId(null); }}
        >
          Next
        </Button>
      </Box>

      <List>
        {venues.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((venue) => (
          <Card key={venue.id} sx={{ mb: 2 }}>
            <ListItem sx={{ py: 2 }}>
              <ListItemText
                sx={{ cursor: 'pointer', flex: 1 }}
                onClick={() => handleToggleExpand(venue.id)}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Business color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {venue.bizName || 'Unnamed Business'}
                    </Typography>
                    <Chip
                      icon={<CheckCircle />}
                      label="Verified"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {venue.bizAddress || 'No address provided'} •{' '}
                    Verified {(venue.verifiedAt || venue.importedAt)?.toDate?.()?.toLocaleDateString?.() || 'Unknown'}
                  </Typography>
                }
              />
              <IconButton
                size="small"
                onClick={() => setPreviewVenue(venue)}
                title="Preview media"
                sx={{ mr: 0.5 }}
              >
                <Visibility />
              </IconButton>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={sendingStripe === venue.id ? <CircularProgress size={14} /> : <Payment />}
                onClick={() => handleSendStripeOnboarding(venue)}
                disabled={sendingStripe === venue.id}
                sx={{ mr: 1, whiteSpace: 'nowrap' }}
              >
                {sendingStripe === venue.id ? 'Sending...' : 'Send Stripe Onboarding'}
              </Button>
              <Box sx={{ cursor: 'pointer' }} onClick={() => handleToggleExpand(venue.id)}>
                {expandedId === venue.id ? <ExpandLess /> : <ExpandMore />}
              </Box>
            </ListItem>

            <Collapse in={expandedId === venue.id} timeout="auto" unmountOnExit>
              <CardContent sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <Business sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Business Details
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      <Typography><strong>Type:</strong> {venue.bizType || 'N/A'}</Typography>
                      <Typography><strong>Cuisine:</strong> {venue.bizCuisines?.join(', ') || 'N/A'}</Typography>
                      <Typography><strong>Average Price:</strong> ${venue.bizAveragePrice || 'N/A'}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Address
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      <Typography>
                        {[venue.bizUnitNo, venue.bizStreetNo, venue.bizStreetName, venue.bizStreetType].filter(Boolean).join(' ')}
                      </Typography>
                      <Typography>
                        {venue.bizSuburb} {venue.bizState} {venue.bizPostCode}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contact Information
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      {venue.bizPhone && (
                        <Typography>
                          <Phone sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {venue.bizPhone}
                        </Typography>
                      )}
                      {venue.bizEmail && (
                        <Typography>
                          <Email sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {venue.bizEmail}
                        </Typography>
                      )}
                      {venue.bizWeb && (
                        <Typography>
                          <Language sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          <a href={venue.bizWeb} target="_blank" rel="noopener noreferrer">{venue.bizWeb}</a>
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Trading Hours
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                        const open = venue[`biz${day}Open`];
                        const close = venue[`biz${day}Close`];
                        return (
                          <Typography key={day} variant="body2">
                            <strong>{day}:</strong> {open && close ? `${open} - ${close}` : 'Closed'}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Collapse>
          </Card>
        ))}
      </List>

      {previewVenue && (
        <MediaPreviewDialog
          open={!!previewVenue}
          onClose={() => setPreviewVenue(null)}
          business={previewVenue}
        />
      )}
    </Box>
  );
};

export default Verified;
